import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';

import type { ReferenceMediaAsset } from '@/apis/library.types';

import { listAiGenerationBatches } from '@/apis/library';
import { logger } from '@/utils/logger';

export interface OriginalGenerationBatch {
  prompt: string | null;
  referenceMedia: ReferenceMediaAsset[];
}

export interface StartedBatch {
  batchId: number;
  galleryId: number;
}

export interface CompletedBatch {
  batchId: number;
  galleryId: number;
  succeeded: number;
  totalJobs: number;
  failed: number;
  moderationBlocked: boolean;
}

export interface CompleteBatchPayload {
  galleryId: number;
  succeeded: number;
  totalJobs: number;
  failed: number;
}

export interface StartedMotion {
  sessionId: string;
  galleryId: number;
}

export interface CompletedMotion {
  sessionId: string;
  galleryId: number;
  status: 'success' | 'failed';
  videoUrl?: string;
  error?: string;
}

export type ActiveBatchesMap = Record<number, { galleryId: number; jobCount: number | null }>;

const AI_MAX_DETERMINISTIC_JOBS = 100;

const clampJobCount = (jobCount: number | null) =>
  jobCount == null ? null : Math.min(jobCount, AI_MAX_DETERMINISTIC_JOBS);

export const useConstellationGenerationStore = defineStore('constellation-generation', () => {
  const activeBatches = ref<ActiveBatchesMap>({});
  const lastStartedBatch = ref<StartedBatch | null>(null);
  const lastCompletedBatch = ref<CompletedBatch | null>(null);
  const moderationBlockedBatches = ref<Record<number, boolean>>({});
  const referenceMediaByGallery = ref<Record<number, ReferenceMediaAsset[]>>({});
  const originalPromptByGallery = ref<Record<number, string | null>>({});
  // Not reactive state — just de-dupes concurrent callers (board page + media
  // drawer chat panel can both ask for the same gallery's original batch).
  const originalBatchRequests = new Map<number, Promise<OriginalGenerationBatch>>();

  const activeMotionSessions = reactive(new Map<string, { galleryId: number }>());
  const lastStartedMotion = ref<StartedMotion | null>(null);
  const lastCompletedMotion = ref<CompletedMotion | null>(null);

  const discoveryCount = ref(0);
  const isDiscovering = computed(() => discoveryCount.value > 0);
  const pendingCompletions = ref(new Map());

  function startBatch(batchId: number, galleryId: number, jobCount: number | null = null) {
    const numericBatchId = Number(batchId);
    const numericGalleryId = Number(galleryId);
    activeBatches.value = {
      ...activeBatches.value,
      [numericBatchId]: { galleryId: numericGalleryId, jobCount: clampJobCount(jobCount) },
    };
    lastStartedBatch.value = { batchId: numericBatchId, galleryId: numericGalleryId };
  }

  function noteActiveBatch(batchId: number, galleryId: number, jobCount: number | null = null) {
    const numericBatchId = Number(batchId);
    if (activeBatches.value[numericBatchId]) return;
    activeBatches.value = {
      ...activeBatches.value,
      [numericBatchId]: { galleryId: Number(galleryId), jobCount: clampJobCount(jobCount) },
    };
  }

  function setReferenceMedia(galleryId: number, assets: ReferenceMediaAsset[]) {
    referenceMediaByGallery.value = {
      ...referenceMediaByGallery.value,
      [Number(galleryId)]: assets,
    };
  }

  function fetchOriginalBatch(
    brandId: number,
    galleryId: number,
  ): Promise<OriginalGenerationBatch> {
    const id = Number(galleryId);
    const cachedPrompt = originalPromptByGallery.value[id];
    const cachedReferenceMedia = referenceMediaByGallery.value[id];
    if (cachedPrompt !== undefined && cachedReferenceMedia !== undefined) {
      return Promise.resolve({ prompt: cachedPrompt, referenceMedia: cachedReferenceMedia });
    }

    const pending = originalBatchRequests.get(id);
    if (pending) return pending;

    // The original prompt is the earliest batch; reference images ride on its
    // inputAssets. References added by later duplication batches are ignored.
    const request = listAiGenerationBatches({
      brandId,
      galleryId: id,
      sortDirection: 'ASC',
      limit: 1,
    })
      .then((response) => {
        const batch = response.data?.data?.[0];
        const prompt = batch?.prompt ?? null;
        const referenceMedia = batch?.inputAssets ?? [];
        originalPromptByGallery.value = { ...originalPromptByGallery.value, [id]: prompt };
        setReferenceMedia(id, referenceMedia);
        return { prompt, referenceMedia };
      })
      .catch((error) => {
        logger.warn(
          'Failed to fetch original AI generation batch',
          { galleryId: id },
          error instanceof Error ? error : new Error(String(error)),
        );
        return { prompt: null, referenceMedia: [] };
      })
      .finally(() => {
        originalBatchRequests.delete(id);
      });

    originalBatchRequests.set(id, request);
    return request;
  }

  function noteBatchModerationBlocked(batchId: number) {
    const numericBatchId = Number(batchId);
    moderationBlockedBatches.value = { ...moderationBlockedBatches.value, [numericBatchId]: true };
  }

  function completeBatch(batchId: number, payload: CompleteBatchPayload) {
    const { galleryId, succeeded, totalJobs, failed } = payload;
    const numericBatchId = Number(batchId);
    const next = { ...activeBatches.value };
    delete next[numericBatchId];
    activeBatches.value = next;

    const moderationBlocked = succeeded === 0 && !!moderationBlockedBatches.value[numericBatchId];
    const nextFlags = { ...moderationBlockedBatches.value };
    delete nextFlags[numericBatchId];
    moderationBlockedBatches.value = nextFlags;

    lastCompletedBatch.value = {
      batchId: numericBatchId,
      galleryId,
      succeeded,
      totalJobs,
      failed,
      moderationBlocked,
    };
  }

  function beginDiscovery() {
    discoveryCount.value += 1;
  }

  function endDiscovery() {
    discoveryCount.value = Math.max(0, discoveryCount.value - 1);
    if (discoveryCount.value === 0 && pendingCompletions.value.size > 0) {
      drainPendingCompletions();
    }
  }

  function bufferCompletion(batchId: number, payload: CompleteBatchPayload) {
    pendingCompletions.value.set(Number(batchId), payload);
  }

  function drainPendingCompletions() {
    const buffered = Array.from(pendingCompletions.value.entries());
    pendingCompletions.value.clear();
    for (const [batchId, payload] of buffered) {
      if (activeBatches.value[batchId]) {
        completeBatch(batchId, payload);
      } else {
        logger.warn(
          `Buffered ai_generation_batch_completed for batch ${batchId} (gallery ${payload.galleryId}) had no matching active batch after discovery — likely a phantom event.`,
        );
      }
    }
  }

  function startMotion(sessionId: string, galleryId: number) {
    activeMotionSessions.set(sessionId, { galleryId });
    lastStartedMotion.value = { sessionId, galleryId };
  }

  function completeMotion(payload: CompletedMotion) {
    activeMotionSessions.delete(payload.sessionId);
    lastCompletedMotion.value = payload;
  }

  return {
    activeBatches,
    lastStartedBatch,
    lastCompletedBatch,
    referenceMediaByGallery,
    originalPromptByGallery,
    startBatch,
    noteActiveBatch,
    setReferenceMedia,
    fetchOriginalBatch,
    noteBatchModerationBlocked,
    completeBatch,
    isDiscovering,
    beginDiscovery,
    endDiscovery,
    bufferCompletion,
    activeMotionSessions,
    lastStartedMotion,
    lastCompletedMotion,
    startMotion,
    completeMotion,
  };
});
